import { ColDef } from 'ag-grid-community';
import { CurrencyDollar } from 'react-bootstrap-icons';
import { useCallback, useMemo, useState } from 'react';
import { BaseReport } from './BaseReport';
import { ReportConfig, FilterField } from './types';
import { ReportsApi } from '../shared/api/axios-client';
import { createApiService } from '../shared/apiConfig';
import { showMoney, cents } from '../Data/Money';
import { ColDefWithCurrency } from './AgingReport';
import {
    ReportOperatorMultiSelectRow,
    ReportOperatorOption,
    useReportOperatorMultiSelect,
} from './ReportOperatorMultiSelect';

const reportApi = createApiService(ReportsApi);

/** Matches API AdminRevenueFacilityRollup (regen OpenAPI to replace with generated type). */
interface AdminRevenueFacilityRollup {
    operatorId?: number;
    operatorName?: string | null;
    facilityId?: number;
    facilityName?: string | null;
    facilityNum?: string | null;
    totalPaid?: number;
    parkerFees?: number;
    facilityFees?: number;
    totalFees?: number;
    reversed?: number;
    remittedToFacility?: number;
    stripeProcessing?: number;
    spotSyncNet?: number;
}

interface AdminRevenueFilter {
    page: number;
    pageSize: number;
    facilityId?: number;
    facilityIds?: number[];
    operatorIds?: number[];
    from?: string;
    to?: string;
    paymentType?: string;
    paymentStatus?: string;
}

const getColumns = (): ColDefWithCurrency<AdminRevenueFacilityRollup>[] => [
    {
        field: 'operatorName',
        headerName: 'Operator',
        hide: false,
        filter: false,
        pinned: 'left',
    },
    {
        field: 'facilityName',
        headerName: 'Facility',
        hide: false,
        filter: false,
        pinned: 'left',
    },
    {
        field: 'totalPaid',
        headerName: 'Total Revenue',
        hide: false,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'totalFees',
        headerName: 'Total Fees',
        hide: false,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'stripeProcessing',
        headerName: 'Stripe Processing',
        hide: false,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'spotSyncNet',
        headerName: 'SpotSync Net',
        hide: false,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'remittedToFacility',
        headerName: 'Remitted To Facility',
        hide: false,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'parkerFees',
        headerName: 'Parker Fees',
        hide: true,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'facilityFees',
        headerName: 'Facility Fees',
        hide: true,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    {
        field: 'reversed',
        headerName: 'Reversed',
        hide: true,
        filter: false,
        valueFormatter: (params) => (params.value ? showMoney(params.value) : '-'),
        isCurrency: true,
    },
    { field: 'facilityNum', headerName: 'Facility #', hide: true, filter: false },
];

const filterFields: FilterField[] = [
    {
        name: 'paymentType',
        label: 'Payment Type',
        type: 'multiselect',
        options: [
            { value: 'Card', label: 'Credit Card' },
            { value: 'ACH', label: 'ACH' },
        ],
    },
    {
        name: 'paymentStatus',
        label: 'Status',
        type: 'select',
        options: [
            { value: 'cleared', label: 'Cleared' },
            { value: 'failed', label: 'ACH Failure' },
            { value: 'reversed', label: 'Reversed' },
            { value: 'processing', label: 'Processing' },
        ],
    },
];

const createConfig = (
    operatorIds?: number[]
): ReportConfig<AdminRevenueFacilityRollup, AdminRevenueFilter> => ({
    id: 'admin-revenue',
    title: 'Revenue Report',
    icon: <CurrencyDollar style={{ fontSize: '1.5rem', marginTop: '-10px' }} />,
    description: `<p>Admin revenue totals by facility across selected operators—including SpotSync fees and net.</p>
            <p>You'll see:</p>
            <ul>
                <li>One row per facility for the selected operators and date filters</li>
                <li>Summed revenue, fees, Stripe processing, SpotSync net, and remitted amounts</li>
            </ul>`,
    defaultColumns: getColumns() as ColDef<AdminRevenueFacilityRollup>[],
    filterFields,
    defaultFilterForm: {
        from: '',
        to: '',
        paymentType: '',
        paymentStatus: '',
        quickDateMode: 'All',
        quickPayoutDateMode: 'All',
        payoutFrom: '',
        payoutTo: '',
        age: '',
    },
    getData: async (filter) => {
        const response = await reportApi.monitorReportsAdminRevenueGet({
            from: filter.from,
            to: filter.to,
            payoutFrom: (filter as any).payoutFrom,
            payoutTo: (filter as any).payoutTo,
            paymentType: filter.paymentType,
            paymentStatus: filter.paymentStatus,
            quickDateMode: (filter as any).quickDateMode,
            quickPayoutDateMode: (filter as any).quickPayoutDateMode,
            operatorIds: operatorIds && operatorIds.length > 0 ? operatorIds : undefined,
        });
        const items = (response.data?.items ?? []) as AdminRevenueFacilityRollup[];
        return {
            items,
            total: items.length,
        };
    },
    transformData: (data: AdminRevenueFacilityRollup[]) =>
        data.map((report) => ({
            ...report,
            totalPaid: cents(report.totalPaid ?? 0),
            parkerFees: cents(report.parkerFees ?? 0),
            facilityFees: cents(report.facilityFees ?? 0),
            totalFees: cents(report.totalFees ?? 0),
            stripeProcessing: cents(report.stripeProcessing ?? 0),
            spotSyncNet: cents(report.spotSyncNet ?? 0),
            remittedToFacility: cents(report.remittedToFacility ?? 0),
            reversed: cents(report.reversed ?? 0),
        })) as any,
});

export function AdminRevenueReport({
    operators,
    heightOffset = 50,
}: {
    operators: ReportOperatorOption[];
    heightOffset?: number;
}) {
    const [savedReports] = useState<never[]>([]);
    const setSavedReports = useCallback(() => {}, []);

    const {
        operatorOptions,
        selectedOptions,
        selectedIds: currentOperatorIds,
        onMultiChange,
        selectAll,
        clearAll,
    } = useReportOperatorMultiSelect(operators);

    const handleReset = useCallback(() => {
        clearAll();
    }, [clearAll]);

    const config = useMemo(
        () => createConfig(currentOperatorIds),
        [currentOperatorIds]
    );

    return (
        <>
            <ReportOperatorMultiSelectRow
                operatorOptions={operatorOptions}
                selectedOptions={selectedOptions}
                onMultiChange={onMultiChange}
                selectAll={selectAll}
                clearAll={clearAll}
            />
            <BaseReport
                config={config}
                savedReports={savedReports}
                setSavedReports={setSavedReports as any}
                onReset={handleReset}
                heightOffset={heightOffset}
                storageKey="report_columns_admin_revenue_facility"
                enableSavedReports={false}
                canRun={currentOperatorIds.length > 0}
                runDisabledTooltip="Select at least one operator to run this report"
            />
        </>
    );
}
